import React, { useEffect } from 'react';
import { createAppKit, CoreHelperUtil } from '@reown/appkit/react';
import { SolanaAdapter } from '@reown/appkit-adapter-solana/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { polygon, solana, mainnet, bsc } from '@reown/appkit/networks';
import { isTelegramMiniApp } from '../utils/platform';

/**
 * Project ID для Reown AppKit (WalletConnect v2).
 *
 * Это проект «Webtransfer» в кабинете dashboard.reown.com (team Webtransfer),
 * создан 06.09.2026. Project ID — не секрет: он в любом случае уезжает в бандл
 * и виден в исходнике страницы у каждого dapp. Поэтому он зашит здесь, а не
 * только в секрете GitHub: секрет REACT_APP_REOWN_PROJECT_ID остаётся как
 * переопределение на случай смены проекта. До 06.09 запасным был проект
 * ROSCASH (137ee4e0…), чей allowlist не включал webtransfer.app — AppKit
 * отвечал «Invalid App Configuration», и с телефона кошельки не подключались.
 */
export const REOWN_PROJECT_ID =
  process.env.REACT_APP_REOWN_PROJECT_ID || '134f3889837ef4fa81db5450a1f3459d';

const metadata = {
  name: "KredaFi",
  description: "Multichain Crypto Wallet & Credit Liquidity Protocol",
  url: typeof window !== "undefined" ? window.location.origin : "https://webtransfer.app",
  icons: ["https://webtransfer.app/assets/kredafi-icon-192.png"],
};

declare global {
  var __WT_APPKIT_INITIALIZED: boolean | undefined;
  var __WT_APPKIT_INSTANCE: ReturnType<typeof createAppKit> | undefined;
  var __WT_APPKIT_ERROR: string | undefined;
}

/** Ленивый старт AppKit — только после входа, не на лендинге (w3m-modal блокирует тапы). */
export function initAppKit(): void {
  if (
    isTelegramMiniApp ||
    !REOWN_PROJECT_ID ||
    typeof window === 'undefined' ||
    globalThis.__WT_APPKIT_INITIALIZED
  ) {
    return;
  }

  try {
    CoreHelperUtil.isTelegram = () => false;

    const evmNetworks = [polygon, mainnet, bsc] as const;
    const wagmiAdapter = new WagmiAdapter({
      networks: [...evmNetworks],
      projectId: REOWN_PROJECT_ID,
    });

    globalThis.__WT_APPKIT_INSTANCE = createAppKit({
      adapters: [new SolanaAdapter(), wagmiAdapter],
      networks: [polygon, solana, mainnet, bsc],
      defaultNetwork: polygon,
      metadata,
      projectId: REOWN_PROJECT_ID,
      themeVariables: {
        '--w3m-z-index': 2000000,
        '--w3m-accent': '#1677ff',
      },
      allowUnsupportedChain: true,
      features: {
        email: false,
        socials: false,
        analytics: false,
        onramp: false,
        swaps: false,
      },
    });

    globalThis.__WT_APPKIT_INITIALIZED = true;
  } catch (err: any) {
    globalThis.__WT_APPKIT_ERROR = err?.message || String(err);
    console.warn('[ReownProvider] Failed to initialize AppKit:', err);
  }
}

/**
 * Сети AppKit по идентификатору сети платформы.
 * ethereum у нас, mainnet у Reown — это одна и та же сеть.
 */
const APPKIT_NETWORKS = {
  polygon,
  bsc,
  ethereum: mainnet,
  solana,
} as const;

export type AppKitNetworkId = keyof typeof APPKIT_NETWORKS;

export function isAppKitNetwork(net: string): net is AppKitNetworkId {
  return net in APPKIT_NETWORKS;
}

/**
 * Пространство имён AppKit по идентификатору сети платформы.
 * Все EVM-сети живут в одном пространстве eip155, Solana — в своём.
 */
const APPKIT_NAMESPACES: Record<AppKitNetworkId, 'eip155' | 'solana'> = {
  polygon: 'eip155',
  bsc: 'eip155',
  ethereum: 'eip155',
  solana: 'solana',
};

export function appKitNamespaceOf(net: string): 'eip155' | 'solana' | undefined {
  return isAppKitNetwork(net) ? APPKIT_NAMESPACES[net] : undefined;
}

/** Пространство имён, активное в AppKit прямо сейчас. */
function activeAppKitNamespace(): string | undefined {
  try {
    return (globalThis.__WT_APPKIT_INSTANCE as any)?.getState?.()?.activeChain;
  } catch {
    return undefined;
  }
}

/** Дождаться, пока активное пространство имён действительно станет нужным. */
async function waitForNamespace(namespace: string, timeoutMs: number): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (activeAppKitNamespace() !== namespace) {
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, 40));
  }
  return true;
}

/**
 * Переключить активную сеть AppKit перед открытием окна подключения.
 *
 * Без этого не подключалась Solana. В ModalController.open() (AppKit 1.8.23)
 * ветки взаимоисключающие:
 *
 *   if (namespace && namespace !== activeChain) { …переключение сети… }
 *   else if (options?.view)                     { RouterController.reset(view) }
 *
 * То есть когда запрошенное пространство имён (solana) не совпадает с активным
 * (eip155 после подключения Polygon / BNB / Ethereum), AppKit переключает сеть
 * и НЕ открывает экран Connect: остаётся прошлый экран, а флаг загрузки для
 * solana так и висит включённым. Снаружи это выглядит как «окно Connect Wallet
 * открылось неактивным, со списком кошельков не той сети, и ничего не делает».
 *
 * Поэтому сначала переключаем сеть отдельным вызовом и только потом открываем
 * окно: пространства имён уже совпадают, и AppKit доходит до reset('Connect').
 *
 * Возвращает true, если пространство имён после вызова действительно нужное.
 */
export async function switchAppKitNetwork(net: string): Promise<boolean> {
  const appKit = globalThis.__WT_APPKIT_INSTANCE;
  const namespace = appKitNamespaceOf(net);
  if (!appKit || !namespace || !isAppKitNetwork(net)) {
    return false;
  }
  if (activeAppKitNamespace() === namespace) {
    return true;
  }
  try {
    await (appKit as any).switchNetwork(APPKIT_NETWORKS[net]);
  } catch (err) {
    // Переключение может не пройти (кошелёк отказал) — подключение всё равно
    // пробуем: хуже, чем сейчас, не станет.
    console.warn('[ReownProvider] switchNetwork failed:', err);
  }
  return waitForNamespace(namespace, 800);
}

/**
 * Открыть экран Connect для сети — так, чтобы он действительно открылся.
 *
 * Одного переключения сети до open() оказалось мало. Переключение — это
 * состояние, которое кто угодно может изменить обратно: восстановление
 * прошлой сессии WalletConnect, синхронизация аккаунта после reconnect,
 * переключение сети самим кошельком. Достаточно, чтобы к моменту open()
 * активным снова стало eip155 — и всё повторяется: AppKit уходит в ветку
 * переключения сети, окно остаётся на том экране, где было («Choose Network»,
 * «Account»), флаг загрузки висит включённым. Снаружи это выглядит как
 * «Connect Wallet открылся неактивным и ничего не делает».
 *
 * Поэтому не полагаемся на то, что переключение удержалось. Пространство имён
 * читается ровно перед open(): по нему ТОЧНО известно, какую из двух веток
 * выберет ModalController. Если он ушёл в переключение сети (эта ветка внутри
 * open() не await'ится), дожидаемся, пока смена завершится, и повторяем
 * open() — второй вызов попадает уже в reset('Connect').
 */
export async function openAppKitConnect(
  net: string,
  open: (opts?: any) => Promise<unknown>,
): Promise<void> {
  const namespace = appKitNamespaceOf(net);
  if (!namespace) {
    throw new Error('connector_unavailable');
  }

  await switchAppKitNetwork(net);

  const before = activeAppKitNamespace();
  await open({ view: 'Connect', namespace });
  if (before === namespace) {
    return;
  }

  // AppKit ушёл в ветку переключения сети. Она не await'ится внутри open(),
  // поэтому сначала дожидаемся смены пространства имён, потом просим экран
  // Connect ещё раз — уже по «правильной» ветке.
  const switched = await waitForNamespace(namespace, 1500);
  if (!switched) {
    // Сюда попадать не должно. Если попали — окно осталось на чужом экране,
    // и это единственный след, по которому в следующий раз можно понять,
    // почему «Connect Wallet» открылся мёртвым.
    console.warn(
      '[ReownProvider] namespace stuck at',
      activeAppKitNamespace(),
      'while opening Connect for',
      namespace,
    );
  }
  await open({ view: 'Connect', namespace });
}

/**
 * Возврат в приложение после подтверждения в кошельке.
 *
 * WalletConnect передаёт кошельку метаданные dapp'а, и в них есть поле
 * redirect: кошелёк, который его поддерживает, после подтверждения сам
 * возвращает человека обратно, а не оставляет его в своём окне с надписью
 * «теперь вернитесь в приложение».
 *
 * AppKit это поле теряет: в initializeUniversalAdapter() он собирает метаданные
 * заново и копирует только name, description, url и icons (см.
 * appkit-base-client.js). Поэтому дописываем redirect прямо в метаданные
 * SignClient — sign-client читает их в момент connect()
 * (`proposer: { publicKey, metadata: this.client.metadata }`), а не при
 * инициализации, так что правка успевает попасть в каждое предложение сессии.
 *
 * Не все кошельки это поддерживают: Trust, SafePal и Binance возвращают,
 * MetaMask на Android — как повезёт, там это решает сам кошелёк.
 */
let redirectPrepared: Promise<void> | null = null;

export function prepareWalletReturn(): Promise<void> {
  if (redirectPrepared) {
    return redirectPrepared;
  }

  const appKit = globalThis.__WT_APPKIT_INSTANCE as any;
  if (!appKit?.getUniversalProvider) {
    return Promise.resolve();
  }

  redirectPrepared = (async () => {
    try {
      const provider = await appKit.getUniversalProvider();
      const metadata = provider?.client?.metadata;
      if (metadata && !metadata.redirect) {
        metadata.redirect = {
          universal: typeof window !== 'undefined' ? window.location.origin : 'https://webtransfer.app',
          linkMode: false,
        };
      }
    } catch (err) {
      console.warn('[ReownProvider] prepareWalletReturn failed:', err);
    }
  })();

  return redirectPrepared;
}

/**
 * Живая проверка relay WalletConnect — с настоящей подписью.
 *
 * Так выглядела предыдущая проверка: открыть `wss://relay.walletconnect.org/
 * ?projectId=<id>&auth=diag` и посчитать отказ признаком негодного projectId.
 * Проверка была бессмысленной. Relay требует подписанный JWT в параметре auth
 * (did:key, ed25519) и отвечает 401 на ЛЮБОЙ запрос без него — с верным
 * projectId, с выдуманным, вовсе без projectId. Проверено запросами к relay:
 * три разных случая, три одинаковых 401. То есть строка «Сокет отклонён.
 * Обычно это неверный или отключённый projectId» горела красным всегда и ни о
 * чём не свидетельствовала.
 *
 * Здесь соединение открывает сам клиент WalletConnect, который AppKit уже
 * поднял: он подписывает auth своим ключом и передаёт настоящий projectId и
 * Origin страницы. Отказ в этом месте — настоящий: либо projectId негоден,
 * либо домен не разрешён в проекте на dashboard.reown.com, либо relay
 * недоступен из этой сети. Успех означает, что предложение сессии дойдёт до
 * кошелька, — то самое, чего нельзя было узнать раньше.
 */
export async function probeWalletConnectRelay(): Promise<{ ok: boolean; detail: string }> {
  const appKit = globalThis.__WT_APPKIT_INSTANCE as any;
  if (!appKit?.getUniversalProvider) {
    return { ok: false, detail: 'AppKit не поднялся — проверять нечем.' };
  }
  try {
    const provider = await appKit.getUniversalProvider();
    const relayer = provider?.client?.core?.relayer;
    if (!relayer) {
      return { ok: false, detail: 'Клиент WalletConnect не создан.' };
    }
    if (relayer.connected) {
      return { ok: true, detail: 'Соединение с relay установлено (подпись принята).' };
    }
    await relayer.transportOpen();
    return relayer.connected
      ? { ok: true, detail: 'Соединение с relay установлено (подпись принята).' }
      : { ok: false, detail: 'Транспорт открыт, но соединение не поднялось.' };
  } catch (err: any) {
    const message = String(err?.message || err);
    // 403 relay отдаёт, когда домен страницы не разрешён в проекте Reown;
    // 401 — когда projectId негоден или отключён.
    const hint = /403|forbidden|origin/i.test(message)
      ? ' Похоже на неразрешённый домен в проекте на dashboard.reown.com.'
      : /401|unauthorized|project/i.test(message)
      ? ' Похоже на негодный или отключённый projectId.'
      : '';
    return { ok: false, detail: message + hint };
  }
}

export function isAppKitReady(): boolean {
  return Boolean(globalThis.__WT_APPKIT_INITIALIZED && globalThis.__WT_APPKIT_INSTANCE);
}

export function appKitInitError(): string | undefined {
  return globalThis.__WT_APPKIT_ERROR;
}

export async function disconnectAllReownWallets(namespace?: 'eip155' | 'solana'): Promise<void> {
  const appKit = globalThis.__WT_APPKIT_INSTANCE;
  if (!appKit) return;
  const namespaces = namespace ? [namespace] : (['eip155', 'solana'] as const);
  for (const ns of namespaces) {
    try {
      await (appKit as any).disconnect(ns);
    } catch {
      /* ignore */
    }
  }
}

export function ReownProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initAppKit();
  }, []);
  return <>{children}</>;
}
